import { Resources } from 'constants/resources';

import { useState } from 'react';

import { usePermissions } from 'hooks/usePermissions';
import { useRecordModal } from 'hooks/useRecordModal';
import {
  StyledCard,
  CardHeaderStyled,
  AlertStyled,
} from 'pages/Orders/OrdersShow/OrdersShowCommonInfo/OrdersShowCommonInfo.styled';
import { useTypedTranslate } from 'providers/i18n/hooks/useTypedTranslate';
import { RecordContextProvider, ReferenceManyField, WithListContext } from 'react-admin';
import { Collapse } from 'shared/mui/Collapse/Collapse';
import { ExpandIconButton } from 'shared/mui/IconButtons';
import { DatagridFieldLabel } from 'shared/mui/Labels/DatagridFieldLabel/DatagridFieldLabel';
import {
  ChipField,
  CurrencyField,
  DateTimeField,
  EmbeddedFunctionField,
  NumberField,
  TextField,
} from 'shared/react-admin/Fields';
import { EmbeddedArrayField } from 'shared/react-admin/Fields/Embedded/EmbeddedArrayField/EmbeddedArrayField';
import { ReferenceOneField } from 'shared/react-admin/ReferenceFields';
import { CommissionShowDialog } from 'shared/widgets/CommissionShowDialog/CommissionShowDialog';
import { Commission } from 'types/embedded/commission';
import { Currency } from 'types/embedded/currency';

import { StyledDatagrid } from './OrdersShowCommonInfoTransactions.styled';
import { OrdersShowCommonInfoTransactionsCommissionChip } from './OrdersShowCommonInfoTransactionsCommissionChip/OrdersShowCommonInfoTransactionsCommissionChip';

export const OrdersShowCommonInfoTransactions = (): JSX.Element => {
  const translate = useTypedTranslate();

  const { hasResourceAccess } = usePermissions();
  const { record, handleOpenRecordModal, handleCloseRecordModal, openRecordModal } =
    useRecordModal<Commission>();

  const [isExpanded, setIsExpanded] = useState(true);

  const handleExpandClick = () => setIsExpanded(!isExpanded);

  return (
    <StyledCard>
      <CardHeaderStyled
        appendActions={
          <ExpandIconButton isExpanded={isExpanded} onClick={handleExpandClick} size="small" />
        }
        isExpanded={isExpanded}
        title={translate('app.orders.transactions.history')}
        titleSize="smallBold"
      />
      <Collapse in={isExpanded} timeout="auto" unmountOnExit>
        <ReferenceManyField
          label={false}
          reference="transactions"
          sort={{ field: 'id', order: 'ASC' }}
          target="order"
        >
          <WithListContext
            render={({ data, isLoading }) => {
              if (!data?.length && !isLoading) {
                return (
                  <AlertStyled severity="info">
                    {translate('app.orders.transactions.noTransactions')}
                  </AlertStyled>
                );
              }

              return (
                <StyledDatagrid bulkActionButtons={false}>
                  <TextField
                    label={translate('app.orders.transactions.transactionId')}
                    source="originId"
                  />
                  <DateTimeField
                    label={translate('app.orders.transactions.createdAt')}
                    source="createdAt"
                  />
                  {hasResourceAccess('transaction_types') && (
                    <ReferenceOneField
                      label={translate('app.orders.transactions.transactionType')}
                      link={false}
                      reference="transaction_types"
                      source="type"
                      target="id"
                    >
                      <ChipField source="name" />
                    </ReferenceOneField>
                  )}
                  <NumberField
                    label={translate('app.orders.transactions.transactionAmount')}
                    options={{ minimumFractionDigits: 2 }}
                    source="amount"
                  />
                  {hasResourceAccess(Resources.Currencies) && (
                    <EmbeddedFunctionField
                      cellClassName="column-currency"
                      label={translate('app.orders.transactions.currency')}
                      render={(value: Currency) => (
                        <RecordContextProvider value={value}>
                          <CurrencyField alphaCodeSource="alphaCode" nameSource="name" />
                        </RecordContextProvider>
                      )}
                      sourceField="currency"
                    />
                  )}
                  {hasResourceAccess(Resources.Commissions) && (
                    <EmbeddedArrayField
                      label={translate('app.orders.transactions.commissionRule')}
                      render={(values: any) =>
                        values.map((v: string) => (
                          <OrdersShowCommonInfoTransactionsCommissionChip
                            id={v}
                            key={v}
                            onClick={(record) => handleOpenRecordModal(record)}
                          />
                        ))
                      }
                      sourceField="commissions"
                    />
                  )}
                  <TextField
                    label={
                      <DatagridFieldLabel
                        primaryText={translate(
                          'app.orders.transactions.commissionAmountOrderCurrency',
                        )}
                        secondaryText={translate('app.orders.transactions.commissionOrderCurrency')}
                      />
                    }
                    source="commissionValueByOrder"
                  />
                  <TextField
                    emptyText={translate('app.orders.transactions.noValue')}
                    label={
                      <DatagridFieldLabel
                        primaryText={translate(
                          'app.orders.transactions.commissionAmountRuleCurrency',
                        )}
                        secondaryText={translate('app.orders.transactions.commissionRuleCurrency')}
                      />
                    }
                    source="commissionValueByRule"
                  />
                </StyledDatagrid>
              );
            }}
          />
        </ReferenceManyField>
      </Collapse>
      {record && (
        <CommissionShowDialog
          commission={record}
          onClose={handleCloseRecordModal}
          open={openRecordModal}
        />
      )}
    </StyledCard>
  );
};
